"use client"

import { useState, useEffect, useCallback } from "react"
import { useTheme, useMediaQuery } from "@mui/material"
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material"
import { Delete as DeleteIcon, Restaurant as RestaurantIcon, AccessTime as AccessTimeIcon } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import apiClient from "../utils/apiClient"
import { format } from "date-fns"
import moment from "moment-timezone"
import { motion, AnimatePresence } from "framer-motion"
import SkeletonLoader from "../components/SkeletonLoader"
import { useSlots } from "../contexts/SlotsContext"

const API_BASE_URL = process.env.REACT_APP_API_URL
const KOLKATA_TIMEZONE = "Asia/Kolkata"

const MyRequestList = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [requests, setRequests] = useState([])
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    requestKey: null,
  })
  const [limit] = useState(10)

  // Updated pagination state for nextKey approach
  const [nextKey, setNextKey] = useState(null)
  const [previousKeys, setPreviousKeys] = useState([]) // Stack to track previous page keys
  const [currentPageNumber, setCurrentPageNumber] = useState(1) // For display purposes
  const [hasNextPage, setHasNextPage] = useState(false)

  const [loading, setLoading] = useState(true)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const { slotsData } = useSlots()

  // Updated fetchRequests to work with nextKey pagination
  const fetchRequests = useCallback(
    async (requestNextKey = null, isNewSearch = false) => {
      setLoading(true)
      try {
        const token = localStorage.getItem("idToken")

        const params = new URLSearchParams({
          limit: limit.toString(),
        })

        // Add nextKey to params if provided
        if (requestNextKey) {
          params.append("nextKey", requestNextKey)
        }

        const response = await apiClient.get(`/requests?${params.toString()}`)

        setRequests(response.data.items)

        // Update pagination state based on new API structure
        const newNextKey = response.data.pagination?.nextKey || null
        setNextKey(newNextKey)
        setHasNextPage(!!newNextKey)

        // Reset pagination tracking for new searches
        if (isNewSearch) {
          setPreviousKeys([])
          setCurrentPageNumber(1)
        }
      } catch (error) {
        console.error("Error fetching requests:", error?.response?.status)
        toast.error("Failed to fetch requests")
      } finally {
        setLoading(false)
      }
    },
    [limit],
  )

  // Initial load - only fetch requests (slots come from context)
  useEffect(() => {
    fetchRequests(null, true)
  }, [])

  const handleDelete = async () => {
    setDeleteLoading(true)
    try {
      const { requestKey } = deleteDialog
      await apiClient.delete(`/requests/delete?requestKey=${encodeURIComponent(requestKey)}`)

      toast.success("Request deleted successfully")
      // Refresh current page after delete
      fetchRequests(previousKeys[previousKeys.length - 1] || null)
    } catch (error) {
      console.error("Error deleting request:", error)
      const message =
        error.response?.status === 400 ? error.response.data?.message || "Bad request" : "Failed to delete request"
      toast.error(message)
    } finally {
      setDeleteLoading(false)
      setDeleteDialog({ open: false, requestKey: null })
    }
  }

  // Updated pagination handlers for nextKey approach
  const handleNextPage = () => {
    if (hasNextPage && nextKey) {
      // Store current position in previous keys stack
      const currentKey = previousKeys[previousKeys.length - 1] || null
      setPreviousKeys((prev) => [...prev, currentKey])
      setCurrentPageNumber((prev) => prev + 1)

      fetchRequests(nextKey)
    }
  }

  const handlePreviousPage = () => {
    if (previousKeys.length > 0) {
      // Get the previous key from stack
      const newPreviousKeys = [...previousKeys]
      const previousKey = newPreviousKeys.pop()
      setPreviousKeys(newPreviousKeys)
      setCurrentPageNumber((prev) => prev - 1)

      fetchRequests(previousKey)
    }
  }

  // Dynamic delete cutoff handling based on slot end times
  function isDeleteDisabled(request) {
    if (!request?.requestDate || !(request?.period || request?.requestType)) return true

    const currentTimeKolkata = moment().tz(KOLKATA_TIMEZONE)
    let requestDateMoment = moment.tz(request.requestDate, "DD/MM/YYYY", KOLKATA_TIMEZONE)
    if (!requestDateMoment.isValid()) {
      requestDateMoment = moment.tz(request.requestDate, "YYYY-MM-DD", KOLKATA_TIMEZONE)
    }

    // Must be today's request
    if (!requestDateMoment.isSame(currentTimeKolkata, 'day')) {
      return true
    }

    const period = request.period || request.requestType

    // Always get slot end time from slots context
    if (!slotsData || !slotsData.slots) {
      // No slots data available - cannot determine if slot has ended
      return true // Disable deletion if we can't verify slot status
    }

    const slot = slotsData.slots.find(s => s.name === period)
    if (!slot) {
      // Slot not found in current slots - might be inactive
      return true // Disable deletion for unknown slots
    }

    // Check if current time is after slot end time
    const [hours, minutes] = slot.endTime.split(':')
    const cutoffTime = requestDateMoment.clone().hour(parseInt(hours)).minute(parseInt(minutes)).second(0).millisecond(0)
    
    return currentTimeKolkata.isAfter(cutoffTime)
  }

  // Helper function to get period background color
  const getPeriodBackground = (period) => {
    if (!period) return "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)"
    if (period.toLowerCase().includes("breakfast")) return "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"
    if (period.toLowerCase().includes("lunch")) return "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)"
    if (period.toLowerCase().includes("dinner")) return "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
    if (period.toLowerCase().includes("evening snacks")) return "linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)"
    if (period === "Breakfast") return "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"
    if (period === "Lunch") return "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)"
    if (period === "Dinner") return "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
    return "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)"
  }

  // Helper function to get meal type icon
  const getMealTypeIcon = (mealType) => {
    if (!mealType) return ""
    if (mealType.toLowerCase() === "veg") return ""
    if (mealType.toLowerCase() === "non-veg") return ""
    return ""
  }

  // Helper function to get meal type color
  const getMealTypeColor = (mealType) => {
    if (!mealType) return "#757575"
    if (mealType.toLowerCase() === "veg") return "#2e7d32"
    if (mealType.toLowerCase() === "non-veg") return "#ed6c02"
    return "#673ab7" // Purple for custom meal types
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  if (loading) {
    return (
      <Container maxWidth="lg">
        <SkeletonLoader type="table" rows={6} />
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3, lg: 4 }, py: { xs: 1, sm: 2 }, pb: { xs: 10, sm: 2 } }}>
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        {/* Mobile-Optimized Header */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              mb: { xs: 2, sm: 3, md: 4 },
              p: { xs: 2, sm: 3, md: 4 },
              background: "linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #3d566e 100%)",
              borderRadius: { xs: 2, sm: 3, md: 4 },
              color: "white",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)",
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: "-50%",
                left: "-50%",
                width: "200%",
                height: "200%",
                background: "conic-gradient(from 180deg, transparent, rgba(255,255,255,0.08), transparent)",
                animation: "rotate 25s linear infinite",
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center"
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, sm: 3 } }}>
                    <RestaurantIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={800}
                      sx={{
                        fontSize: { xs: "1.1rem", sm: "1.5rem", md: "2rem" },
                        whiteSpace: "nowrap",
                        background: "linear-gradient(45deg, #fff 30%, #ffe0b2 90%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: { xs: 0.5, sm: 1 },
                        lineHeight: 1.2,
                      }}
                    >
                      My Food Requests
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        opacity: 0.9,
                        fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
                        display: { xs: "none", sm: "block" }
                      }}
                    >
                      Manage your personal food requests
                    </Typography>
                  </Box>
                </Box>
                <motion.div
                  whileTap={{ scale: 0.95 }}
                  style={{
                    background: "linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)",
                    borderRadius: "16px",
                    backdropFilter: "blur(15px)",
                    border: "1px solid rgba(255,255,255,0.3)",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => navigate("/requests/new")}
                    sx={{
                      bgcolor: "transparent",
                      color: "white",
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: { xs: "13px", sm: "14px" },
                      px: { xs: 2.5, sm: 3 },
                      py: { xs: 1, sm: 1.25 },

                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "transparent",
                        boxShadow: "0 8px 32px rgba(255,255,255,0.25)",
                      },
                    }}
                  >
{isMobile ? "New" : "New Request"}
                  </Button>
                </motion.div>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Mobile-Optimized Table */}
        <motion.div variants={itemVariants}>
          <TableContainer
            component={Paper}
            className="mobile-table"
            sx={{
              borderRadius: { xs: 2, sm: 3, md: 4 },
              overflow: "auto",
              background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.3)",
              boxShadow: "0 25px 80px rgba(0,0,0,0.12)",
              // Mobile-specific improvements
              WebkitOverflowScrolling: "touch",
              maxWidth: "100%",
              // Better mobile scrolling
              '&::-webkit-scrollbar': {
                height: { xs: 4, sm: 6 },
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(32, 213, 213, 0.5)',
                borderRadius: 3,
                '&:hover': {
                  background: 'rgba(32, 213, 213, 0.7)',
                },
              },
            }}
          >
            <Table sx={{ 
              tableLayout: { xs: 'auto', md: 'fixed' }, 
              width: '100%', 
              minWidth: { xs: '700px', sm: '100%' }
            }}>
              <TableHead>
                <TableRow
                  sx={{
                    background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 50%, #0891b2 100%)",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "3px",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
                    },
                  }}
                >
                  <TableCell sx={{ 
                    py: { xs: 1.5, sm: 2, md: 3 }, 
                    px: { xs: 1, sm: 2 },
                    color: "white", 
                    fontWeight: 700, 
                    fontSize: { xs: "12px", sm: "14px", md: "16px" }, 
                    width: { xs: 'auto', md: '12%' }, 
                    minWidth: { xs: '90px', sm: '100px' }
                  }}>Date</TableCell>
                  <TableCell sx={{ 
                    py: { xs: 1.5, sm: 2, md: 3 }, 
                    px: { xs: 1, sm: 2 },
                    color: "white", 
                    fontWeight: 700, 
                    fontSize: { xs: "12px", sm: "14px", md: "16px" }, 
                    width: { xs: 'auto', md: '15%' }, 
                    minWidth: { xs: '110px', sm: '120px' }
                  }}>Submitted</TableCell>
                  <TableCell sx={{ 
                    py: { xs: 1.5, sm: 2, md: 3 }, 
                    px: { xs: 1, sm: 2 },
                    color: "white", 
                    fontWeight: 700, 
                    fontSize: { xs: "12px", sm: "14px", md: "16px" }, 
                    width: { xs: 'auto', md: '12%' }, 
                    minWidth: { xs: '70px', sm: '80px' }
                  }}>Type</TableCell>
                  <TableCell sx={{ 
                    py: { xs: 1.5, sm: 2, md: 3 }, 
                    px: { xs: 1, sm: 2 },
                    color: "white", 
                    fontWeight: 700, 
                    fontSize: { xs: "12px", sm: "14px", md: "16px" }, 
                    width: { xs: 'auto', md: '10%' }, 
                    minWidth: { xs: '70px', sm: '80px' }
                  }}>Meal</TableCell>
                  <TableCell sx={{ 
                    py: { xs: 1.5, sm: 2, md: 3 }, 
                    px: { xs: 1, sm: 2 },
                    color: "white", 
                    fontWeight: 700, 
                    fontSize: { xs: "12px", sm: "14px", md: "16px" }, 
                    width: { xs: 'auto', md: '40%' }, 
                    minWidth: { xs: '140px', sm: '150px' }
                  }}>Reason</TableCell>
                  <TableCell sx={{ 
                    py: { xs: 1.5, sm: 2, md: 3 }, 
                    px: { xs: 1, sm: 2 },
                    color: "white", 
                    fontWeight: 700, 
                    fontSize: { xs: "12px", sm: "14px", md: "16px" }, 
                    width: { xs: 'auto', md: '11%' }, 
                    minWidth: { xs: '70px', sm: '80px' }
                  }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AnimatePresence>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 12 }}>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <Box
                            sx={{
                              background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                              borderRadius: 4,
                              p: 8,
                              border: "3px dashed #cbd5e1",
                            }}
                          >
                            <motion.div
                              animate={{
                                y: [0, -15, 0],
                                rotate: [0, 5, -5, 0],
                              }}
                              transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY }}
                            >
                              <Typography variant="h1" sx={{ mb: 3 }}>
                              </Typography>
                            </motion.div>
                            <Typography variant="h4" fontWeight={800} color="text.primary" gutterBottom>
                              No requests found
                            </Typography>
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                              You haven't made any food requests yet
                            </Typography>
                            <motion.div whileHover={{ scale: 1.0 }} whileTap={{ scale: 0.95 }}>
                              <Button
                                variant="contained"
                                onClick={() => navigate("/requests/new")}
                                sx={{
                                  borderRadius: 3,
                                  fontWeight: 700,
                                  px: 4,
                                  py: 1.5,
                                  background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                                  boxShadow: "0 8px 32px rgba(32, 213, 213, 0.3)",
                                }}
                              >
                                Create Your First Request
                              </Button>
                            </motion.div>
                          </Box>
                        </motion.div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests.map((request, index) => (
                      <motion.tr
                        key={request.requestKey}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 30 }}
                        transition={{ delay: index * 0.05 }}
                        component={TableRow}
                        sx={{
                          position: "relative",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, rgba(32, 213, 213, 0.08) 0%, rgba(44, 62, 80, 0.08) 100%)",
                            transform: "scale(1.01)",
                            "& .action-buttons": {
                              transform: "scale(1.1)",
                            },
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: "3%",
                            right: "3%",
                            height: "1px",
                            background: "linear-gradient(90deg, transparent, rgba(0,0,0,0.08), transparent)",
                          },
                        }}
                      >
                        <TableCell sx={{ py: { xs: 1.5, sm: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1, md: 2 } }}>
                            <motion.div
                              animate={{ rotate: [0, 360] }}
                              transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                            >
                              <AccessTimeIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 }, color: "#20d5d5" }} />
                            </motion.div>
                            <Typography
                              variant="body1"
                              fontWeight={700}
                              sx={{
                                fontSize: { xs: "12px", sm: "14px", md: "16px" },
                                background: "linear-gradient(45deg, #20d5d5 30%, #16B1C8 90%)",
                                backgroundClip: "text",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                              }}
                            >
                              {request.requestDate
                                ? format(
                                    // Accept both formats
                                    moment(request.requestDate, ["DD-MM-YYYY"]).toDate(),
                                    "MMM dd, yyyy",
                                  )
                                : ""}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1.5, sm: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
                          <Typography 
                            variant="body2" 
                            fontWeight={600}
                            sx={{ fontSize: { xs: "11px", sm: "12px", md: "14px" } }}
                          >
                            {format(new Date(request.createdAt * 1000), "MMM dd, yyyy HH:mm")}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1.5, sm: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
                          <Chip
                            label={request.period || request.requestType || "Unknown"}
                            size={"small"}
                            sx={{
                              background: getPeriodBackground(request.period || request.requestType),
                              fontWeight: 700,
                              fontSize: { xs: "10px", sm: "12px", md: "13px" },
                              height: { xs: 24, sm: 28, md: 32 },
                              borderRadius: { xs: 2, sm: 3 },
                              border: "1px solid rgba(255,255,255,0.4)",
                              backdropFilter: "blur(10px)",
                              '& .MuiChip-label': {
                                px: { xs: 1, sm: 1.5 },
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1.5, sm: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
                            <Typography
                              variant="body1"
                              fontWeight={700}
                              sx={{
                                fontSize: { xs: "11px", sm: "13px", md: "16px" },
                                color: getMealTypeColor(request.mealType),
                              }}
                            >
                              {getMealTypeIcon(request.mealType)} {request.mealType || "Unknown"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1.5, sm: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
                          {request.reason ? (
                            <Tooltip title={request.reason} arrow placement="top" sx={{ maxWidth: 400 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: { xs: "11px", sm: "12px", md: "14px" },
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  cursor: "pointer",
                                  maxWidth: { xs: "120px", sm: "200px", md: "none" },
                                  "&:hover": {
                                    color: "#20d5d5",
                                    fontWeight: 600,
                                  },
                                }}
                              >
                                {request.reason}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                fontStyle: "italic",
                                fontSize: { xs: "11px", sm: "12px", md: "14px" }
                              }}
                            >
                              No reason
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ py: { xs: 1.5, sm: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
                          <Box
                            className="action-buttons"
                            sx={{
                              display: "flex",
                              gap: { xs: 0.5, sm: 1 },
                              justifyContent: "center",
                              transition: "transform 0.3s ease",
                            }}
                          >
                            <Tooltip
                              title={
                                isDeleteDisabled(request)
                                  ? "The slot has ended. Request cannot be deleted."
                                  : "Delete Request"
                              }
                              arrow
                            >
                              <span>
                                <motion.div
                                  animate={false} // Prevent initial dancing!
                                  whileHover={!isDeleteDisabled(request) ? { scale: 1.04, rotate: -4, y: -2 } : {}}
                                  whileTap={{ scale: 0.97 }}
                                >
                                  <IconButton
                                    size={"small"}
                                    onClick={() =>
                                      setDeleteDialog({
                                        open: true,
                                        requestKey: request.requestKey,
                                      })
                                    }
                                    disabled={isDeleteDisabled(request)}
                                    sx={{
                                      width: { xs: 32, sm: 36, md: 40 },
                                      height: { xs: 32, sm: 36, md: 40 },
                                      background: isDeleteDisabled(request)
                                        ? "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)"
                                        : "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)",
                                      color: isDeleteDisabled(request) ? "grey.500" : "white",
                                      borderRadius: { xs: 1.5, sm: 2 },
                                      "&:hover": {
                                        background: !isDeleteDisabled(request)
                                          ? "linear-gradient(135deg, #f44336 0%, #ff5722 100%)"
                                          : undefined,
                                        boxShadow: !isDeleteDisabled(request)
                                          ? "0 8px 25px rgba(211, 47, 47, 0.4)"
                                          : undefined,
                                      },
                                      "&:disabled": {
                                        background: "grey.300",
                                        color: "grey.500",
                                      },
                                    }}
                                  >
                                    <DeleteIcon
                                      sx={{ 
                                        fontSize: { xs: 14, sm: 16, md: 18 }, 
                                        color: isDeleteDisabled(request) ? "#bdbdbd" : "white" 
                                      }}
                                    />
                                  </IconButton>
                                </motion.div>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>
        </motion.div>

        {/* Mobile-Optimized Pagination */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: "row",
              gap: 2,
              mt: { xs: 2, sm: 3, md: 4 },
              p: { xs: 2, sm: 3 },
              background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
              backdropFilter: "blur(20px)",
              borderRadius: { xs: 2, sm: 3 },
              border: "1px solid rgba(255,255,255,0.3)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.1)",
            }}
          >
            <motion.div whileTap={{ scale: 0.95 }}>
              <Button
                onClick={handlePreviousPage}
                disabled={previousKeys.length === 0 || loading}
                variant="contained"
                sx={{
                  borderRadius: { xs: 2, sm: 3 },
                  fontWeight: 700,
                  fontSize: { xs: "13px", sm: "14px" },
                  px: { xs: 2.5, sm: 3 },
                  py: { xs: 0.75, sm: 1 },
                  background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
                  boxShadow: "0 8px 25px rgba(44, 62, 80, 0.3)",
                  "&:disabled": {
                    background: "linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)",
                    boxShadow: "none",
                  },
                }}
              >
                ← Previous
              </Button>
            </motion.div>
            <Box
              sx={{
                background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                borderRadius: { xs: 2, sm: 3 },
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1, sm: 1.25 },
                color: "white",
                boxShadow: "0 8px 25px rgba(32, 213, 213, 0.3)",

              }}
            >
              <Typography 
                variant="h6" 
                fontWeight={800}
                sx={{ fontSize: { xs: "14px", sm: "16px" } }}
              >
                Page {currentPageNumber}
              </Typography>
            </Box>
            <motion.div whileTap={{ scale: 0.95 }}>
              <Button
                onClick={handleNextPage}
                disabled={!hasNextPage || loading}
                variant="contained"
                sx={{
                  borderRadius: { xs: 2, sm: 3 },
                  fontWeight: 700,
                  fontSize: { xs: "13px", sm: "14px" },
                  px: { xs: 2.5, sm: 3 },
                  py: { xs: 0.75, sm: 1 },
                  background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
                  boxShadow: "0 8px 25px rgba(44, 62, 80, 0.3)",
                  "&:disabled": {
                    background: "linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)",
                    boxShadow: "none",
                  },
                }}
              >
                Next →
              </Button>
            </motion.div>
          </Box>
        </motion.div>
      </motion.div>
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleteLoading && setDeleteDialog({ open: false, requestKey: null })}
        PaperProps={{
          borderRadius: 4,
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255,255,255,0.3)",
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            background: "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)",
            color: "white",
            borderRadius: "16px 16px 0 0",
          }}
        >
          🗑️ Delete Request
        </DialogTitle>
        <DialogContent sx={{ py: 4 }}>
          <Typography variant="h6" fontWeight={600}>
            Are you sure you want to delete this request?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              onClick={() => setDeleteDialog({ open: false, requestKey: null })}
              disabled={deleteLoading}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Cancel
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              onClick={handleDelete}
              disabled={deleteLoading}
              variant="contained"
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                background: "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)",
                boxShadow: "0 8px 25px rgba(211, 47, 47, 0.3)",
              }}
            >
              {deleteLoading ? "🔄 Deleting..." : "🗑️ Delete"}
            </Button>
          </motion.div>
        </DialogActions>
      </Dialog>
      <style jsx>
        {`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}
      </style>
    </Container>
  )
}

export default MyRequestList
